/**
 * Render the question-paper preview into an <iframe> for on-screen print preview
 * AND native printing.
 *
 * The export path (generateQuestionPaperPdf) rasterises each sheet via
 * html2canvas; printing instead feeds the real DOM to the browser's own print
 * engine, so KaTeX math, Bengali text and fonts render exactly as on screen with
 * no rasterisation artefacts.
 *
 * We render via a Blob object URL (`iframe.src = URL.createObjectURL(...)`) — a
 * *complete* same-origin document — rather than `iframe.srcdoc` or
 * `document.write`. srcdoc gives the iframe an `about:srcdoc` document URL, and
 * Chrome's print pipeline renders `about:srcdoc` frames BLANK even though they
 * display fine on screen (a long-standing Chromium bug). A Blob URL yields a real
 * `blob:` document on the page's own origin, which prints reliably and resolves
 * relative stylesheet/font hrefs correctly. The URL is stashed on the iframe
 * (`iframe._printBlobUrl`) so the caller can revoke it on unmount; we keep it
 * alive while the modal is open in case the print pipeline re-fetches the src.
 * Callers show the iframe in a preview modal and later call
 * `iframe.contentWindow.print()` — printing the iframe prints only its document,
 * with no `@media print` body-hiding gymnastics on the host page.
 *
 * @param {HTMLElement}       sourceElement  the preview root (contains the `[data-paper-page]` sheets)
 * @param {HTMLIFrameElement} iframe         target iframe to populate
 * @param {object}            [opts]
 * @param {string}            [opts.title]       document title (used as the print / PDF name)
 * @param {'a4'|'a5'|'joint'} [opts.pageFormat]  paper size ('joint' = A4 landscape, two A5 page columns)
 * @param {number}            [opts.contentPx]   on-screen sheet width to lay out at (WYSIWYG)
 */
// Remove every top-level `@page` rule and every `@media print` block from a CSS
// string (leaving all screen rules untouched), so foreign print styles copied
// from other features can't hijack the question-paper print document. Brace-aware
// so nested rules inside `@media print { … }` are dropped as a whole.
function stripPrintBlocks(css) {
    let out = '';
    for (let i = 0; i < css.length;) {
        if (css[i] === '@' && /^@(page|media)\b/i.test(css.slice(i, i + 7))) {
            let j = i;
            while (j < css.length && css[j] !== '{' && css[j] !== ';') j++;
            if (css[j] === '{') {
                const prelude = css.slice(i, j);
                let depth = 0, k = j;
                for (; k < css.length; k++) {
                    if (css[k] === '{') depth++;
                    else if (css[k] === '}') { depth--; if (depth === 0) { k++; break; } }
                }
                const drop = /^@page\b/i.test(prelude) || (/^@media\b/i.test(prelude) && /\bprint\b/i.test(prelude));
                if (drop) { i = k; continue; }      // skip the whole block
                out += css.slice(i, k); i = k; continue;   // keep non-print block verbatim
            }
        }
        out += css[i++];
    }
    return out;
}

export async function renderPaperIntoIframe(sourceElement, iframe, { title, pageFormat = 'a4', contentPx } = {}) {
    if (!sourceElement || !iframe) throw new Error('Preview element is not ready.');

    const isJoint = pageFormat === 'joint';
    const CONTENT_PX = Math.round(contentPx) || (isJoint ? 1024 : pageFormat === 'a5' ? 512 : 720);
    const pageNodes = sourceElement.querySelectorAll('[data-paper-page]');
    const nodes = pageNodes.length ? [...pageNodes] : [sourceElement];

    // Bring over the app's stylesheets (Tailwind, KaTeX, fonts) so the clone is styled,
    // but STRIP every foreign `@page` and `@media print` block first. Other features
    // inject global print CSS (generate-image/print.css, PrintModal.module.scss,
    // ats-cv-preview.scss, global.scss) that redefine `@page` (e.g. `size: 595px 842px;
    // margin: 0`) and hide body children (`body > * { display:none }`). Copied into this
    // iframe they fight our own print rules and break the printed sheet. We keep each
    // sheet's screen rules and drop only its @page / print-media blocks; the pageStyle
    // below then supplies the one correct print layout.
    const headStyles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
        .map((el) => {
            if (el.tagName === 'STYLE') return `<style>${stripPrintBlocks(el.textContent || '')}</style>`;
            // In the production build the app CSS ships as external <link href="/assets/*.css">.
            // Read its rules (same-origin → allowed) and inline them so stripPrintBlocks runs and
            // the styles are guaranteed to apply inside the blob iframe (a bare copied <link> can
            // fail to style it, and stripPrintBlocks never ran on it → foreign print rules leaked).
            // Cross-origin sheets (Google Fonts) throw on cssRules — keep those as a plain <link>.
            try {
                const rules = el.sheet && el.sheet.cssRules;
                if (rules) return `<style>${stripPrintBlocks([...rules].map((r) => r.cssText).join('\n'))}</style>`;
            } catch { /* cross-origin — fall through to the <link> */ }
            return el.outerHTML;
        })
        .join('\n');

    // We copy ALL app stylesheets (headStyles) to inherit Tailwind/KaTeX/fonts, but
    // that also drags in unrelated global @media print rules from other features —
    // notably generate-image's print.css, which does
    //   body > *:not(#print-clone) { display: none !important }   (specificity 1,0,1)
    //   html, body { overflow: hidden !important }
    // Inside this iframe the `.print-sheet`s ARE <body>'s direct children, so that
    // rule hides them ALL when printing → blank pages (screen preview is unaffected
    // since it's @media print only). We give the body an id and re-assert the sheets
    // with selectors of HIGHER specificity (#id > .class = 1,1,0 beats 1,0,1) so our
    // rules win the cascade regardless of what else is copied in.
    const pageStyle = `<style>
        @page { size: ${isJoint ? 'A4 landscape' : `${pageFormat === 'a5' ? 'A5' : 'A4'} portrait`}; margin: 8mm; }
        html, body { margin: 0; padding: 0; background: #e5e7eb; }
        body { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 16px; }
        .print-sheet { width: ${CONTENT_PX}px; max-width: 100%; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,.18); }
        [data-dnd-handle] { display: none !important; }
        @media print {
            html, body { background: #fff; overflow: visible !important; }
            #qp-print-body { display: block !important; overflow: visible !important; padding: 0; gap: 0; }
            #qp-print-body > .print-sheet { display: block !important; width: 100%; box-shadow: none; }
            #qp-print-body > .print-sheet + .print-sheet { break-before: page; page-break-before: always; }
        }
    </style>`;

    // Build cleaned body markup by cloning each sheet into a detached holder.
    const holder = document.createElement('div');
    nodes.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.classList.remove('hidden');
        // Joint sheets are flex rows (two page columns) — keep that layout.
        clone.style.display = isJoint ? 'flex' : 'block';
        clone.style.width = `${CONTENT_PX}px`;
        clone.style.maxWidth = '100%';
        // Centre the fixed-width page within the (wider) printable sheet so the
        // leftover space is split evenly — otherwise the whole paper hugs the left
        // and all the slack piles up on the right. Matches the PDF export, which
        // centres each page too. On screen the sheet == page width, so auto → 0.
        clone.style.marginLeft = 'auto';
        clone.style.marginRight = 'auto';
        clone.style.transform = 'none';
        clone.style.boxShadow = 'none';

        // Strip screen-only affordances (drag handles) so they don't print.
        clone.querySelectorAll('[data-dnd-handle]').forEach((el) => el.remove());

        // Drop optional header fields (e.g. Time) that were left blank.
        clone.querySelectorAll('[data-export-optional]').forEach((el) => {
            const input = el.querySelector('input[data-export-text]');
            if (input && !input.value.trim()) el.remove();
        });

        // Replace inline-editable header inputs with plain text (no input chrome).
        // Done before serialising so the typed value survives (outerHTML drops the
        // live `value` property). The span must keep the input's inline styles
        // (template font size / line height) and its inline-block display: on a
        // plain inline span `width` is ignored, so the w-full institution and
        // exam-title lines would shrink to text width and merge onto ONE line.
        clone.querySelectorAll('input[data-export-text]').forEach((el) => {
            const span = document.createElement('span');
            span.textContent = el.value.trim() || el.getAttribute('data-export-fallback') || '';
            span.className = el.className;
            span.style.cssText = el.style.cssText;
            span.style.display = 'inline-block';
            el.replaceWith(span);
        });

        const sheet = document.createElement('div');
        sheet.className = 'print-sheet';
        sheet.appendChild(clone);
        holder.appendChild(sheet);
    });

    const titleTag = title ? `<title>${String(title).replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'))}</title>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${titleTag}${headStyles}${pageStyle}</head><body id="qp-print-body">${holder.innerHTML}</body></html>`;

    // Serve the document from a Blob object URL (not srcdoc) so it prints reliably.
    // Revoke any URL from a previous render on this iframe before minting a new one.
    if (iframe._printBlobUrl) URL.revokeObjectURL(iframe._printBlobUrl);
    const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    iframe._printBlobUrl = blobUrl;

    // Wait for the document to finish loading before we (or the caller's print()) run.
    await new Promise((resolve) => {
        const onLoad = () => { iframe.removeEventListener('load', onLoad); resolve(); };
        iframe.addEventListener('load', onLoad, { once: true });
        iframe.src = blobUrl;
    });

    // Let fonts settle so nothing prints blank / with fallback metrics.
    try { await iframe.contentDocument?.fonts?.ready; } catch { /* non-fatal */ }
}
